import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import DialogBox from '../common/DialogBox';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { Party } from '../../shared/interfaces';
import { isCashParty } from '../../utils/cashParty';

interface PartyListReferenceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const PartyListReferenceDialog: React.FC<PartyListReferenceDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const location = useLocation();
  const hideCashParty = location.pathname.startsWith('/game/');
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const lastFocusedInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Store the last focused input before opening dialog
      const activeElement = document.activeElement;
      if (activeElement?.tagName === 'INPUT') {
        lastFocusedInputRef.current = activeElement as HTMLInputElement;
      }
      fetchParties();
      setSelectedIndex(-1); // Reset selection when dialog opens
    } else {
      // Reset search when dialog closes
      setSearchTerm('');
      setSelectedIndex(-1);
      lastFocusedInputRef.current = null;
    }
  }, [isOpen]);

  // Reset selection when search term changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchTerm]);

  // Scroll selected row into view
  const scrollToRow = React.useCallback((index: number) => {
    const row = rowRefs.current.get(index);
    if (row && listContainerRef.current) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  // Select party and set it in the active input field
  const selectParty = React.useCallback((party: Party) => {
    if (hideCashParty && isCashParty(party)) {
      return;
    }
    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('party-selected-from-reference', {
      detail: {
        partyCode: party.partyCode,
        partyName: party.partyName,
        partyId: party.id
      }
    }));

    // Try to find the party input field
    // First, try to find input with id or placeholder containing "party" (case insensitive)
    let targetInput: HTMLInputElement | null = null;
    
    // Method 1: Look for input with placeholder containing "Party Code" or "party"
    const partyInputs = document.querySelectorAll('input[placeholder*="Party"], input[placeholder*="party"], input[placeholder*="Party Code"]');
    for (const input of Array.from(partyInputs)) {
      const htmlInput = input as HTMLInputElement;
      if (htmlInput.offsetParent !== null && // Visible
          !htmlInput.placeholder.toLowerCase().includes('search')) {
        targetInput = htmlInput;
        break;
      }
    }
    
    // Method 2: If not found, look for input with id containing "party"
    if (!targetInput) {
      const inputById = document.querySelector('input[id*="party"], input[id*="Party"]') as HTMLInputElement;
      if (inputById && inputById.offsetParent !== null) {
        targetInput = inputById;
      }
    }
    
    // Method 3: Use the last focused input before dialog opened (most reliable)
    if (!targetInput && lastFocusedInputRef.current) {
      const lastInput = lastFocusedInputRef.current;
      if (lastInput.offsetParent !== null) { // Still visible
        targetInput = lastInput;
      }
    }
    
    // Method 4: If still not found, look for inputs that are likely party code inputs
    if (!targetInput) {
      const allInputs = document.querySelectorAll('input[type="text"]');
      for (const input of Array.from(allInputs)) {
        const htmlInput = input as HTMLInputElement;
        if (htmlInput.offsetParent !== null && // Visible
            htmlInput !== document.querySelector('input[placeholder*="Search"]') &&
            (htmlInput.maxLength === 0 || htmlInput.maxLength <= 50)) { // Party codes are typically short
          targetInput = htmlInput;
          break;
        }
      }
    }

    // If we found a target input, set the value
    if (targetInput) {
      targetInput.value = `${party.partyCode} - ${party.partyName}`;
      // Trigger input and change events so React components pick it up
      const inputEvent = new Event('input', { bubbles: true });
      const changeEvent = new Event('change', { bubbles: true });
      targetInput.dispatchEvent(inputEvent);
      targetInput.dispatchEvent(changeEvent);
      // Also trigger React's synthetic events
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(targetInput, `${party.partyCode} - ${party.partyName}`);
        targetInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      // Focus the input
      setTimeout(() => {
        targetInput?.focus();
        // Move cursor to end
        if (targetInput) {
          targetInput.setSelectionRange(targetInput.value.length, targetInput.value.length);
        }
      }, 10);
    }

    // Close the dialog
    onClose();
  }, [hideCashParty, onClose]);

  // Filter parties based on search term
  const filteredParties = React.useMemo(() => {
    return parties.filter(party => {
      const searchLower = searchTerm.toLowerCase();
      return (
        party.partyCode.toLowerCase().includes(searchLower) ||
        party.partyName.toLowerCase().includes(searchLower)
      );
    });
  }, [parties, searchTerm]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle keys when typing in search input
      const activeElement = document.activeElement;
      if (activeElement?.tagName === 'INPUT' && activeElement !== document.querySelector('input[placeholder*="Search"]')) {
        return;
      }

      // If search input is focused, only handle Escape
      if (activeElement?.tagName === 'INPUT' && activeElement === document.querySelector('input[placeholder*="Search"]')) {
        if (e.key === 'Escape') {
          onClose();
          return;
        }
        // Allow arrow keys to navigate list even when search is focused
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          // Focus will move to list
        } else {
          return; // Let search input handle other keys
        }
      }

      if (filteredParties.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => {
            const next = prev < filteredParties.length - 1 ? prev + 1 : prev;
            scrollToRow(next);
            return next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => {
            const next = prev > 0 ? prev - 1 : (prev === -1 ? filteredParties.length - 1 : 0);
            scrollToRow(next);
            return next;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filteredParties.length) {
            selectParty(filteredParties[selectedIndex]);
          } else if (filteredParties.length === 1) {
            // If only one party, select it
            selectParty(filteredParties[0]);
          } else if (filteredParties.length > 0 && selectedIndex === -1) {
            // If nothing selected, select first one
            selectParty(filteredParties[0]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, filteredParties, selectedIndex, onClose, scrollToRow, selectParty]);

  const fetchParties = async () => {
    setLoading(true);
    try {
      const response = await apiService.post<{ data: Party[]; total: number }>(apiRoutes.parties.lite, {
        page: 1,
        pageSize: 10000, // Fetch all parties
        orderBy: 'partyCode',
        orderDirection: 'asc'
      });
      
      const partiesData = response.data || [];
      const filteredParties = partiesData.filter((p: Party) => !hideCashParty || !isCashParty(p));
      setParties(filteredParties);
    } catch (error) {
      console.error('Error fetching parties:', error);
      setParties([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogBox isOpen={isOpen} onClose={onClose} title="Party List Reference (F3)">
      <div className="bg-white dark:bg-slate-900 p-4" style={{ width: '600px', maxWidth: '90vw', height: '70vh', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        {/* Search Input */}
        <div className="mb-4 flex-shrink-0">
          <input
            type="text"
            placeholder="Search by party code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            autoFocus
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-300 border-t-transparent"></div>
            <span className="ml-3 text-gray-600 dark:text-slate-400">Loading parties...</span>
          </div>
        )}

        {/* Party List */}
        {!loading && (
          <div 
            ref={listContainerRef}
            className="flex-1 overflow-y-auto min-h-0 border border-gray-200 dark:border-slate-700 rounded-md"
            tabIndex={0}
          >
            {filteredParties.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                {searchTerm ? 'No parties found matching your search' : 'No parties available'}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-100 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-slate-300">Party Code</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-slate-300">Party Name</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredParties.map((party, index) => {
                    const isSelected = index === selectedIndex;
                    return (
                    <tr
                      key={party.id}
                        ref={(el) => {
                          if (el) {
                            rowRefs.current.set(index, el);
                          } else {
                            rowRefs.current.delete(index);
                          }
                        }}
                        onClick={() => {
                          setSelectedIndex(index);
                          selectParty(party);
                        }}
                        className={`cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-blue-100 dark:bg-blue-900/50 border-l-4 border-blue-500' 
                            : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'
                        }`}
                    >
                      <td className="px-4 py-2 text-gray-900 dark:text-slate-100 font-medium">
                        {party.partyCode}
                      </td>
                      <td className="px-4 py-2 text-gray-700 dark:text-slate-300">
                        {party.partyName}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Footer Info */}
        {!loading && (
          <div className="mt-4 flex-shrink-0 text-xs text-gray-500 dark:text-slate-400 text-center">
            Showing {filteredParties.length} of {parties.length} parties
            {searchTerm && ` (filtered by "${searchTerm}")`}
          </div>
        )}
      </div>
    </DialogBox>
  );
};

export default PartyListReferenceDialog;

